/**
 * AttentionCentreWidget (9.9b)
 * Shows summary counts of items needing attention
 */
import { useBallInCourt, BallInCourtData } from '@/hooks/useDashboardReports';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Target, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export function AttentionCentreWidget() {
  const { data, isLoading, error } = useBallInCourt();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Attention Centre
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 animate-pulse">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Attention Centre
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load attention items</p>
        </CardContent>
      </Card>
    );
  }

  const ballInCourtData = data as BallInCourtData;
  const summary = ballInCourtData.summary || {
    waiting_on_us_count: 0,
    waiting_on_us_urgent: 0,
    waiting_on_them_count: 0,
    waiting_on_them_urgent: 0,
  };

  const totalItems = summary.waiting_on_us_count + summary.waiting_on_them_count;

  return (
    <Card data-testid="attention-centre-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Target className="h-5 w-5" />
            Attention Centre
          </CardTitle>
          <Link
            to="/reports?tab=ball-in-court"
            className="text-xs text-slate-600 hover:underline flex items-center gap-1"
          >
            View all
            <ArrowRight size={12} />
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-orange-50 rounded-lg p-3">
            <p className="text-xs text-orange-700 font-medium">Waiting on Us</p>
            <p className="text-2xl font-bold text-orange-900">{summary.waiting_on_us_count}</p>
            {summary.waiting_on_us_urgent > 0 && (
              <Badge variant="destructive" className="mt-1 text-xs">
                {summary.waiting_on_us_urgent} urgent
              </Badge>
            )}
          </div>
          <div className="bg-violet-50 rounded-lg p-3">
            <p className="text-xs text-violet-700 font-medium">Waiting on Others</p>
            <p className="text-2xl font-bold text-violet-900">{summary.waiting_on_them_count}</p>
            {summary.waiting_on_them_urgent > 0 && (
              <Badge variant="secondary" className="mt-1 text-xs">
                {summary.waiting_on_them_urgent} urgent
              </Badge>
            )}
          </div>
        </div>

        {/* Total Summary */}
        <div className="border-t pt-3 flex justify-between items-center text-sm">
          <span className="text-muted-foreground">
            Total items requiring attention
          </span>
          <span className="font-semibold">{totalItems}</span>
        </div>
      </CardContent>
    </Card>
  );
}
